/**
 * Submission Triage — the user's Saved Views and last layout (OR-1384).
 *
 * Owns both halves of the stored blob: the named views the user can
 * re-apply, and the layout each tab was last left in. Both tabs are
 * fetched up front (two small parallel GETs) because the page keeps
 * state for both in memory and switching tabs must not hit the network.
 *
 * The backend PUT is a **full replacement** of one tab's blob, which
 * drives three rules here:
 *
 *  1. **Never write to a tab whose read failed.** A failed GET used to
 *     degrade into an empty config; saving one view would then replace
 *     the user's whole list with just that view. A tab that could not be
 *     read stays read-only for the session, and `replace` reports failure
 *     so the page can surface it.
 *  2. **One write at a time per tab.** Two PUTs in flight can land out of
 *     order and leave the server holding the older blob, so writes are
 *     chained per tab.
 *  3. **Roll back to what the server actually holds.** A failed write must
 *     not clobber a later successful one, so each mutation carries a
 *     revision and only undoes itself while it is still the latest. It
 *     restores the last list the server confirmed, not the local value it
 *     replaced, which may itself have been an optimistic list that never
 *     landed.
 *
 * Reads otherwise fail quietly into "no saved views": preferences are a
 * convenience and must never take Triage down.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { VIEW_KEYS, type ViewKey, getViewPreference, putViewPreference } from "../api";
import {
    type LastLayout,
    type SavedView,
    fitsConfigCap,
    parseLastLayout,
    parseSavedViews,
    serializeBlob,
} from "../pages/submissions-list/view-preference-config";
import type { TriageView } from "../pages/submissions-list/triage-columns";

const KEY_BY_VIEW: Record<TriageView, ViewKey> = {
    submissions: VIEW_KEYS.triageSubmissions,
    facilities: VIEW_KEYS.triageFacilities,
};

const TABS: TriageView[] = ["submissions", "facilities"];

export type SavedViewsByTab = Record<TriageView, SavedView[]>;
export type LayoutByTab = Record<TriageView, LastLayout | null>;

export interface SavedViewsState {
    /** `loading` until the initial fetch settles — success or failure. */
    status: "loading" | "ready";
    views: SavedViewsByTab;
    /** The layout each tab was last left in (OR-1384). Seeds the page. */
    lastLayout: LayoutByTab;
    /** Per tab: whether its blob can be written. False for a tab whose
     *  initial read failed, since a full-replacement PUT over a blob we
     *  never read would destroy whatever it holds. The page uses this to
     *  keep that tab's controls disabled rather than let edits be dropped
     *  silently. */
    canWrite: Record<TriageView, boolean>;
    /** Replace one tab's list. Applies optimistically, rolls back if the
     *  write fails and nothing newer has happened since; resolves to
     *  whether it persisted. Returns false without touching anything when
     *  the tab's blob could not be read. */
    replace: (tab: TriageView, next: SavedView[]) => Promise<boolean>;
    /** Record the layout the tab is now in. Fire-and-forget: this is a
     *  convenience, never something to interrupt the user over. */
    rememberLayout: (tab: TriageView, layout: LastLayout) => void;
}

const EMPTY: SavedViewsByTab = { submissions: [], facilities: [] };
const NO_LAYOUT: LayoutByTab = { submissions: null, facilities: null };

export function useSavedViews(): SavedViewsState {
    const [status, setStatus] = useState<"loading" | "ready">("loading");
    const [views, setViews] = useState<SavedViewsByTab>(EMPTY);
    const [lastLayout, setLastLayout] = useState<LayoutByTab>(NO_LAYOUT);
    const [canWrite, setCanWrite] = useState<Record<TriageView, boolean>>({ submissions: false, facilities: false });

    /** Per tab: did its initial read actually succeed? Rule 1. */
    const readable = useRef<Record<TriageView, boolean>>({ submissions: false, facilities: false });
    /** Mirror the state so a write can rebuild the whole blob — writing
     *  one half alone would erase the other. */
    const viewsRef = useRef<SavedViewsByTab>(EMPTY);
    const layoutRef = useRef<LayoutByTab>(NO_LAYOUT);
    /** Per-tab write chain. Rule 2. */
    const queue = useRef<Record<TriageView, Promise<unknown>>>({
        submissions: Promise.resolve(),
        facilities: Promise.resolve(),
    });
    /** Per-tab mutation counter. Rule 3. */
    const revision = useRef<Record<TriageView, number>>({ submissions: 0, facilities: 0 });
    /** The last list each tab's blob was confirmed to hold on the server.
     *  Rollback target, so a failed write cannot leave the page showing a
     *  list that was never persisted. */
    const persisted = useRef<SavedViewsByTab>(EMPTY);

    useEffect(() => {
        let cancelled = false;
        // `allSettled`, not a `catch` that yields `{}`: we have to be able
        // to tell "this user has no saved views" from "we could not read
        // them", because only the first is safe to write over.
        Promise.allSettled(TABS.map((tab) => getViewPreference(KEY_BY_VIEW[tab]))).then((results) => {
            if (cancelled) return;
            const nextViews: SavedViewsByTab = { ...EMPTY };
            const nextLayout: LayoutByTab = { ...NO_LAYOUT };
            TABS.forEach((tab, i) => {
                const result = results[i];
                readable.current[tab] = result.status === "fulfilled";
                const config = result.status === "fulfilled" ? result.value : {};
                nextViews[tab] = parseSavedViews(tab, config);
                nextLayout[tab] = parseLastLayout(tab, config);
            });
            viewsRef.current = nextViews;
            layoutRef.current = nextLayout;
            persisted.current = nextViews;
            setViews(nextViews);
            setLastLayout(nextLayout);
            setCanWrite({ ...readable.current });
            setStatus("ready");
        });
        return () => {
            cancelled = true;
        };
    }, []);

    /** Run `task` after whatever is already queued for that tab. */
    const enqueue = useCallback((tab: TriageView, task: () => Promise<void>) => {
        const next = queue.current[tab].then(task, task);
        queue.current[tab] = next;
        return next;
    }, []);

    const replace = useCallback(
        async (tab: TriageView, next: SavedView[]): Promise<boolean> => {
            if (!readable.current[tab]) return false;
            // Refuse locally rather than let the backend 422 the whole blob —
            // a rejected write would lose every view, not just the new one.
            if (!fitsConfigCap(next, tab, layoutRef.current[tab])) return false;

            const mine = ++revision.current[tab];
            viewsRef.current = { ...viewsRef.current, [tab]: next };
            setViews(viewsRef.current);

            let landed = false;
            await enqueue(tab, async () => {
                try {
                    await putViewPreference(KEY_BY_VIEW[tab], serializeBlob(next, layoutRef.current[tab], tab));
                    persisted.current = { ...persisted.current, [tab]: next };
                    landed = true;
                } catch {
                    // Only undo while this is still the newest mutation, and
                    // fall back to what the server is known to hold.
                    if (revision.current[tab] === mine) {
                        viewsRef.current = { ...viewsRef.current, [tab]: persisted.current[tab] };
                        setViews(viewsRef.current);
                    }
                }
            });
            return landed;
        },
        [enqueue],
    );

    const rememberLayout = useCallback(
        (tab: TriageView, layout: LastLayout) => {
            if (!readable.current[tab]) return;
            if (!fitsConfigCap(viewsRef.current[tab], tab, layout)) return;
            layoutRef.current = { ...layoutRef.current, [tab]: layout };
            revision.current[tab] += 1;
            void enqueue(tab, async () => {
                // Read the refs at send time so this carries the latest list
                // and layout rather than a stale snapshot.
                const sent = viewsRef.current[tab];
                try {
                    await putViewPreference(KEY_BY_VIEW[tab], serializeBlob(sent, layoutRef.current[tab], tab));
                    // That PUT carried the list too, so the server now holds it.
                    persisted.current = { ...persisted.current, [tab]: sent };
                } catch {
                    // Dropped silently: the next commit re-sends the layout.
                }
            });
        },
        [enqueue],
    );

    return { status, views, lastLayout, canWrite, replace, rememberLayout };
}
